"use client";

import type { OrgType } from "@atlas/computation";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { adminKeys } from "@/lib/query-keys";

interface AdminOrg {
  id: string;
  name: string;
  slug: string | null;
  membersCount: number;
  enabledModules: string[];
  createdAt: number;
  hasProfile: boolean;
  orgType?: OrgType;
  region?: string;
  state?: string;
}

interface AdminOrgDetail {
  organization: AdminOrg & { availableModules: string[] };
  members: Array<{
    userId: string;
    email: string | null;
    firstName: string | null;
    lastName: string | null;
    role: string;
    createdAt: number;
  }>;
}

export function useAdminOrganizations(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: adminKeys.organizations(),
    queryFn: async () => {
      const res = await api.admin.organizations.$get();
      const json = await res.json();
      if (!res.ok) throw new Error("Failed to load organizations");
      return (
        json as {
          success: boolean;
          data: { organizations: AdminOrg[]; totalCount: number };
        }
      ).data;
    },
    enabled: options?.enabled ?? true,
  });
}

export function useAdminOrganization(orgId: string) {
  return useQuery({
    queryKey: adminKeys.organization(orgId),
    queryFn: async () => {
      const res = await api.admin.organizations[":orgId"].$get({
        param: { orgId },
      });
      const json = await res.json();
      if (!res.ok) throw new Error("Failed to load organization");
      return (json as { success: boolean; data: AdminOrgDetail }).data;
    },
    enabled: !!orgId,
  });
}

export function useUpdateOrgModules(orgId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (enabledModules: string[]) => {
      const res = await api.admin.organizations[":orgId"].modules.$patch({
        param: { orgId },
        json: { enabledModules },
      });
      const json: unknown = await res.json().catch(() => null);
      if (
        !res.ok ||
        !json ||
        typeof json !== "object" ||
        !("success" in json) ||
        json.success !== true
      ) {
        const message =
          json &&
          typeof json === "object" &&
          "error" in json &&
          typeof json.error === "string" &&
          json.error.trim()
            ? json.error
            : "Failed to update modules";
        throw new Error(message);
      }
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: adminKeys.organization(orgId),
          exact: true,
        }),
        queryClient.invalidateQueries({ queryKey: adminKeys.organizations() }),
      ]);
    },
  });
}

export function useCreateOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      name: string;
      enabledModules: string[];
      orgType?: string;
    }) => {
      const res = await api.admin.organizations.$post({
        json: {
          name: params.name,
          enabledModules: params.enabledModules,
          orgType: params.orgType ?? "community_health_system",
        },
      });
      const json = await res.json();
      if (!res.ok) throw new Error("Failed to create organization");
      return json as {
        success: boolean;
        data: {
          organization: {
            id: string;
            name: string;
            slug: string | null;
            enabledModules: string[];
          };
          profile: { id: string; orgId: string };
        };
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.organizations() });
    },
  });
}

interface AdminInvitation {
  id: string;
  emailAddress: string;
  role: string;
  status: "pending" | "accepted" | "revoked" | "expired";
  createdAt: number;
  updatedAt: number;
}

export function useAdminOrgInvitations(orgId: string) {
  return useQuery({
    queryKey: adminKeys.invitations(orgId),
    queryFn: async () => {
      const res = await api.admin.organizations[":orgId"].invitations.$get({
        param: { orgId },
      });
      const json = await res.json();
      if (!res.ok) throw new Error("Failed to load invitations");
      return (
        json as {
          success: boolean;
          data: { invitations: AdminInvitation[]; totalCount: number };
        }
      ).data;
    },
    enabled: !!orgId,
  });
}

export function useInviteOrgMember(orgId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      emailAddress: string;
      role: "org:admin" | "org:member";
    }) => {
      const res = await api.admin.organizations[":orgId"].invitations.$post({
        param: { orgId },
        json: {
          emailAddress: params.emailAddress,
          role: params.role,
        },
      });
      const json = (await res.json()) as
        | { success: true; data: { invitation: AdminInvitation } }
        | { success: false; error: string };
      if (!res.ok || !json.success) {
        const message =
          "error" in json && json.error ? json.error : "Failed to send invite";
        throw new Error(message);
      }
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminKeys.invitations(orgId),
      });
      queryClient.invalidateQueries({
        queryKey: adminKeys.organization(orgId),
      });
    },
  });
}

export function useRevokeOrgInvitation(orgId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invitationId: string) => {
      const res = await api.admin.organizations[":orgId"].invitations[
        ":invitationId"
      ].$delete({
        param: { orgId, invitationId },
      });
      const json = (await res.json()) as
        | { success: true; data: { invitationId: string } }
        | { success: false; error: string };
      if (!res.ok || !json.success) {
        const message =
          "error" in json && json.error
            ? json.error
            : "Failed to revoke invitation";
        throw new Error(message);
      }
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminKeys.invitations(orgId),
      });
    },
  });
}

export function useProvisionOrgProfile(orgId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params?: { orgType?: string }) => {
      const res = await api.admin.organizations[":orgId"].provision.$post({
        param: { orgId },
        json: { orgType: params?.orgType ?? "community_health_system" },
      });
      const json = await res.json();
      if (!res.ok) throw new Error("Failed to provision profile");
      return json as {
        success: boolean;
        data: { profileId: string; alreadyExisted: boolean };
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminKeys.organization(orgId),
      });
      queryClient.invalidateQueries({ queryKey: adminKeys.organizations() });
    },
  });
}
